import {observer} from "mobx-react";
import {useEffect, useReducer, useState} from "react";
import styled from "styled-components";
import type {
	ValidateBoundaryDetailsValidateObjectDto,
	ValidateBoundaryRequest,
	ValidateXyiconDetailsValidateObjectDto,
	ValidateXyiconRequest,
} from "../../../generated/api/base";
import {XyiconFeature} from "../../../generated/api/base";
import {useAppStore} from "../../../StateManager";
import {DetailsContentV5} from "../details/DetailsContentV5";
import {DetailsContentV5HeaderStyled} from "../details/DetailsContentV5HeaderStyled";
import InfoIcon from "../icons/info.svg?react";
import {LoaderV5} from "../loader/LoaderV5";
import {XHRLoader} from "../../../utils/loader/XHRLoader";
import {DebugInformation} from "../../../utils/DebugInformation";
import type {INotAvailableInfo} from "./ValidationUtils";
import {getLoadingInfo, processValidationResult, ReasonForNotAvailable} from "./ValidationUtils";

const DebugDetailsViewRefreshListTime = "DetailsView refresh list time";

interface IErrorMessageInDetailsViewProps {
	readonly notAvailableInfo: INotAvailableInfo;
}

const ErrorMessageInDetailsView = (props: IErrorMessageInDetailsViewProps) => {
	const {notAvailableInfo} = props;

	const getContent = () => {
		switch (notAvailableInfo.reason) {
			case ReasonForNotAvailable.Loading:
				return <LoaderV5 />;
			default:
				return (
					<>
						{notAvailableInfo.reason !== ReasonForNotAvailable.DifferentPortfolio && <InfoIcon />}
						{notAvailableInfo.title && <ErrorMessageTitleStyled>{notAvailableInfo.title}</ErrorMessageTitleStyled>}
						{notAvailableInfo.details && <ErrorMessageDetailsStyled>{notAvailableInfo.details}</ErrorMessageDetailsStyled>}
					</>
				);
		}
	};

	return <NotAvailableContentStyled>{getContent()}</NotAvailableContentStyled>;
};

const getFeatureFromURL = (moduleName: string) => {
	if (moduleName === "xyicon") {
		return XyiconFeature.Xyicon;
	} else if (moduleName === "boundary") {
		return XyiconFeature.Boundary;
	}

	return null;
};

export const DetailsViewV5 = observer(() => {
	const appState = useAppStore((state) => state.appState);
	const {activePortfolio, activePortfolioRefId, actions} = appState;
	const [isPortTemplateEditorOpen, setPortTemplateEditorOpen] = useState<boolean>(false);
	const [notAvailableInfo, setNotAvailableInfo] = useState<INotAvailableInfo>(getLoadingInfo());
	const [, forceUpdate] = useReducer((x) => x + 1, 0);
	// #app/fb3b6307-1491-4828-99ea-d6b849d60a77/p3/details/xyicon/x32
	const splittedHash = window.location.hash.split("/");
	const objectRefIdFromHash = splittedHash[5];

	useEffect(() => {
		const onHashChange = () => {
			forceUpdate();
		};
		window.addEventListener("hashchange", onHashChange);

		return () => {
			window.removeEventListener("hashchange", onHashChange);
		};
	}, [forceUpdate]);

	const moduleName = splittedHash[4];
	const feature = getFeatureFromURL(moduleName);
	const {transport, navigation} = appState.app;

	const objectMaybe = actions.getFeatureItemByRefId(objectRefIdFromHash, feature);
	const isListLoaded = appState.lists[feature].loaded;
	const featureService = transport.services.feature;

	useEffect(() => {
		if (!isListLoaded) {
			const debugText = `${DebugDetailsViewRefreshListTime}-${activePortfolioRefId}-${feature}`;
			DebugInformation.start(debugText);
			featureService.refreshList(feature).then(() => {
				DebugInformation.end(debugText);
			});
		}
	}, [feature, featureService, isListLoaded, activePortfolioRefId]);

	useEffect(() => {
		if (!objectMaybe) {
			const updateErrorMessage = async () => {
				switch (feature) {
					case XyiconFeature.Xyicon:
						{
							const params: ValidateXyiconRequest = {
								xyiconRefID: objectRefIdFromHash,
							};
							const {result, error} = await transport.requestForOrganization<ValidateXyiconDetailsValidateObjectDto>({
								url: "xyicons/validate",
								method: XHRLoader.METHOD_POST,
								params,
							});

							const newNotAvailableInfo = processValidationResult("Xyicon", appState, result, error);
							setNotAvailableInfo(newNotAvailableInfo);
						}
						break;
					case XyiconFeature.Boundary:
						{
							const params: ValidateBoundaryRequest = {
								boundaryRefID: objectRefIdFromHash,
							};
							const {result, error} = await transport.requestForOrganization<ValidateBoundaryDetailsValidateObjectDto>({
								url: "boundaries/validate",
								method: XHRLoader.METHOD_POST,
								params,
							});

							const newNotAvailableInfo = processValidationResult("Boundary", appState, result, error);
							setNotAvailableInfo(newNotAvailableInfo);
						}
						break;
				}
			};
			updateErrorMessage();
		}
	}, [objectRefIdFromHash, objectMaybe, feature, activePortfolio, moduleName, actions, navigation, transport, appState]);

	if (feature) {
		if (objectMaybe) {
			return (
				<DetailsViewStyled>
					<DetailsContentWrapper>
						<DetailsContentV5
							items={[objectMaybe]}
							feature={feature}
							features={[feature]}
							isPortTemplateEditorOpen={isPortTemplateEditorOpen}
							setPortTemplateEditorOpen={setPortTemplateEditorOpen}
						/>
					</DetailsContentWrapper>
				</DetailsViewStyled>
			);
		} else {
			return <ErrorMessageInDetailsView notAvailableInfo={notAvailableInfo} />;
		}
	}
});

const NotAvailableContentStyled = styled.div`
	> svg {
		width: 36px;
		height: 36px;
	}
	width: 100%;
	display: flex;
	align-items: center;
	justify-content: center;
	flex-direction: column;
	gap: 16px;
`;

const ErrorMessageTitleStyled = styled.div`
	font-size: 32px;
	line-height: 40px;
`;

const ErrorMessageDetailsStyled = styled.div`
	font-size: 18px;
	line-height: 24px;
`;

const DetailsWidth = 800;

const DetailsContentWrapper = styled.div`
	min-width: 200px;
	width: ${DetailsWidth}px;
	overflow-y: auto;
`;

const DetailsViewStyled = styled.div`
	display: flex;
	justify-content: center;
	width: 100%;
	position: relative;
	${DetailsContentV5HeaderStyled} {
		&.anchored {
			width: ${DetailsWidth}px;
			left: 50%;
			transform: translateX(-50%);
		}
	}
`;
